import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters, Panel, KpiCard, Loading, Empty, PageHeader, ExportBar, exportExcel, exportCSV } from "./_shared";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area, BarChart, Bar, Cell,
} from "recharts";
import { Users, CheckCircle2, Gauge, Star, Repeat, Clock, Timer, Award } from "lucide-react";

const tip = { contentStyle: { borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 } };

export default function Overview() {
  const { query } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/overview${query}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [query]);

  if (loading) return <Loading />;
  if (!data) return <Empty />;
  const k = data.kpis;
  const hasData = k.total_participants > 0;

  const handleExcel = () =>
    exportExcel([
      { name: "KPIs", rows: [k] },
      { name: "Competency Radar", rows: data.competency_radar },
      { name: "Score Trend", rows: data.score_trend },
    ], "overview.xlsx");

  return (
    <div data-testid="overview-page">
      <PageHeader title="Dashboard Overview"
        description="A live snapshot of overall learning progress across your selected population."
        right={<ExportBar onExcel={handleExcel} onCSV={() => exportCSV(data.competency_radar, "overview-competencies.csv")} />} />

      {!hasData ? <Empty /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard testid="kpi-participants" label="Total Participants" value={k.total_participants} icon={Users} />
            <KpiCard testid="kpi-roleplays" label="Role Plays Completed" value={k.total_roleplays_completed} icon={CheckCircle2} accent="#0f766e" />
            <KpiCard testid="kpi-completion" label="Completion %" value={`${k.completion_pct}%`} icon={Gauge} accent="#b45309" />
            <KpiCard testid="kpi-avgscore" label="Average AI Score" value={k.avg_ai_score} icon={Star} accent="#1d4ed8" />
            <KpiCard label="Avg Attempts / Scenario" value={k.avg_attempts_per_scenario} icon={Repeat} accent="#4d7c0f" />
            <KpiCard label="Avg Duration" value={`${k.avg_duration_min}m`} icon={Clock} accent="#be185d" />
            <KpiCard label="Practice Hours" value={`${k.practice_hours}h`} icon={Timer} accent="#0369a1" />
            <KpiCard testid="kpi-cert" label="Certification Readiness" value={`${k.certification_readiness_pct}%`} icon={Award} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Panel title="Completion Trend" subtitle="Role plays completed per week" testid="completion-trend">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.completion_trend}>
                  <defs>
                    <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#b81f24" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#b81f24" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#a8a29e" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#a8a29e" allowDecimals={false} />
                  <Tooltip {...tip} />
                  <Area type="monotone" dataKey="count" stroke="#b81f24" strokeWidth={2} fill="url(#cg)" name="Completed" />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Average Score Trend" subtitle="Mean AI score per week" testid="score-trend">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.score_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#a8a29e" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#a8a29e" />
                  <Tooltip {...tip} />
                  <Line type="monotone" dataKey="value" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} name="Avg score" />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <Panel title="Competency Radar" subtitle="Average across competencies" className="lg:col-span-1" testid="competency-radar">
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={data.competency_radar} outerRadius="72%">
                  <PolarGrid stroke="#e7e5e4" />
                  <PolarAngleAxis dataKey="competency" tick={{ fontSize: 10, fill: "#57534e" }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#d6d3d1" />
                  <Radar dataKey="score" stroke="#b81f24" fill="#b81f24" fillOpacity={0.25} strokeWidth={2} />
                  <Tooltip {...tip} />
                </RadarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Top Strengths" subtitle="Highest scoring competencies" testid="top-strengths">
              <div className="space-y-3">
                {data.top_strengths.map((s) => (
                  <div key={s.competency}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-stone-700">{s.competency}</span>
                      <span className="font-semibold tabular-nums text-emerald-700">{s.score}</span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-600" style={{ width: `${s.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Top Development Areas" subtitle="Lowest scoring competencies" testid="top-dev-areas">
              <div className="space-y-3">
                {data.top_development_areas.map((s) => (
                  <div key={s.competency}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-stone-700">{s.competency}</span>
                      <span className="font-semibold tabular-nums text-[#b81f24]">{s.score}</span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#b81f24]" style={{ width: `${s.score}%` }} />
                    </div>
                  </div>
                ))}
                {data.common_gaps?.length > 0 && (
                  <div className="pt-3 mt-2 border-t border-stone-100">
                    <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Most common gaps</div>
                    <div className="flex flex-wrap gap-1.5">
                      {data.common_gaps.slice(0, 5).map((g) => (
                        <span key={g.gap} className="text-[11px] bg-stone-100 text-stone-600 rounded-md px-2 py-1">{g.gap}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
